import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
	MultiSelect,
	MultiSelectContent,
	MultiSelectItem,
	MultiSelectTrigger,
} from "@/components/ui/MultiSelect";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { UploadDropzone } from "@/components/ui/UploadDropzone";
import cacheKeys from "@/config/cache-keys";
import { languageOptions } from "@/config/language-options";
import { LanguageOptionLabel } from "@/components/shared/LanguageFlag";
import { capitalizeFirstLetter, removeExtension } from "@/lib/utils";
import AppService from "@/services/AppService";
import { DOCUMENT_CATEGORIES } from "@/types/document/document";
import type { CreateDocument, DocumentLanguage } from "@/types/document/document";
import type { Group } from "@/types/group/group";
import { useQueryClient } from "@tanstack/react-query";
import {
	ArrowUpCircleIcon,
	CheckIcon,
	FileIcon,
	FilePlus2Icon,
	PenLineIcon,
	TrashIcon,
	UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";

const appService = new AppService();

interface UploadDocumentsButtonProps {
	groups: Group[];
}

export const UploadDocumentsButton = ({
	groups,
}: UploadDocumentsButtonProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { accountId } = useParams();

	// --- 1. Types and Props ---
	type Category = (typeof DOCUMENT_CATEGORIES)[number];

	// --- 2. State ---
	const [isOpen, setIsOpen] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<Category>();
	const [selectedLanguage, setSelectedLanguage] = useState<DocumentLanguage>();
	const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
	const [selectedDocuments, setSelectedDocuments] = useState<CreateDocument[]>(
		[]
	);

	// --- 3. Derived data ---
	const groupOptions = groups.map((group) => ({
		value: group.id,
		label: group.name,
	}));

	const categoryOptions = DOCUMENT_CATEGORIES.map((category) => ({
		value: category,
		label: t(`calculator.documents.categories.${category}`),
	}));

	// --- 4. Handlers ---
	const handleFilenameChange = (name: string, index: number) => {
		setSelectedDocuments((docs) =>
			docs.map((doc, docIndex) => (docIndex === index ? { ...doc, name } : doc))
		);
	};

	const handleUpload = async () => {
		if (!accountId)
			return toast.error(t("admin.upload_messages.no_account_found"));
		if (!selectedCategory)
			return toast.error(t("admin.upload_messages.select_category"));
		if (!selectedLanguage)
			return toast.error(t("admin.upload_messages.select_language"));
		if (selectedGroups.size === 0)
			return toast.error(t("admin.upload_messages.select_group"));
		if (selectedDocuments.length === 0)
			return toast.error(t("admin.upload_messages.select_file"));
		if (selectedDocuments.length > 10)
			return toast.error(t("admin.upload_messages.max_files_exceeded"));

		try {
			const promise = appService.uploadMultipleDocuments(
				accountId,
				Array.from(selectedGroups),
				selectedCategory,
				selectedLanguage,
				selectedDocuments
			);

			toast.promise(promise, {
				loading: t("admin.upload_messages.uploading_documents"),
				success: t("admin.upload_messages.documents_uploaded_success"),
				error: t("admin.upload_messages.document_upload_error"),
			});

			await promise;

			setSelectedDocuments([]);
			setSelectedGroups(new Set());
			setSelectedCategory(undefined);
			setSelectedLanguage(undefined);
			setIsOpen(false);
			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error uploading documents:", error);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogTrigger asChild>
				<Button size="sm">
					<FilePlus2Icon /> {t("admin.dialog.button.doc-upload")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[750px] max-h-[90vh]">
				<DialogHeader>
					<DialogTitle>
						{t("admin.dialog.upload-documents.doc-title")}
					</DialogTitle>
					<DialogDescription>
						{t("admin.dialog.upload-documents.doc-content")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-2">
						<div className="flex flex-col gap-2">
							<Select
								value={selectedCategory}
								onValueChange={(value) =>
									setSelectedCategory(value as Category)
								}
							>
								<SelectTrigger>
									<SelectValue
										placeholder={t("admin.dialog.upload-documents.category")}
									/>
								</SelectTrigger>
								<SelectContent>
									{categoryOptions.map((category) => (
										<SelectItem
											key={category.value}
											value={category.value}
											className="hover:bg-soft-white justify-start"
										>
											{category.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-2">
							<Select
								value={selectedLanguage}
								onValueChange={(value) =>
									setSelectedLanguage(value as DocumentLanguage)
								}
							>
								<SelectTrigger className="[&>span]:inline-flex [&>span]:items-center">
									<SelectValue
										placeholder={t("admin.dialog.upload-documents.language")}
									/>
								</SelectTrigger>
								<SelectContent>
									{languageOptions.map((option) => (
										<SelectItem
											key={option.value}
											value={option.value}
											className="hover:bg-soft-white justify-start"
										>
											<LanguageOptionLabel
												language={option.value}
												label={option.iso6392}
											/>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<MultiSelect>
							<MultiSelectTrigger
								options={groupOptions}
								selectedValues={selectedGroups}
								name={t("admin.dialog.upload-documents.groups")}
								className="w-full justify-start"
								icon={<UsersIcon />}
							/>
							<MultiSelectContent
								clearSelected={() => setSelectedGroups(new Set())}
								selectedSize={selectedGroups.size}
								className="pointer-events-auto"
							>
								{groupOptions.map((option) => (
									<MultiSelectItem
										key={option.value}
										option={option}
										isSelected={selectedGroups.has(option.value)}
										toggleValue={() => {
											const newSelected = new Set(selectedGroups);
											if (newSelected.has(option.value)) {
												newSelected.delete(option.value);
											} else {
												newSelected.add(option.value);
											}
											setSelectedGroups(newSelected);
										}}
									/>
								))}
							</MultiSelectContent>
						</MultiSelect>
					</div>
					{selectedDocuments.length === 0 && (
						<UploadDropzone
							onFilesAccepted={(files) => {
								if (files.length > 10) {
									toast.error("Maks 10 filer kan lastes opp samtidig.");
								}
								const limitedDocs = files.slice(0, 10).map(
									(file) =>
										({
											file: file,
											name: capitalizeFirstLetter(removeExtension(file.name)),
										} as CreateDocument)
								);
								setSelectedDocuments(limitedDocs);
							}}
						/>
					)}
					{selectedDocuments.length > 0 && (
						<div className="overflow-y-auto max-h-40 flex flex-col gap-2">
							{selectedDocuments.map((file, index) => {
								return (
									<UploadedFileCard
										key={`${file.name}-${index}`}
										file={file}
										handleFilenameChange={(name) => {
											handleFilenameChange(name, index);
										}}
										deleteFile={() => {
											setSelectedDocuments((prevDocs) =>
												prevDocs.filter((_, i) => i !== index)
											);
										}}
									/>
								);
							})}
						</div>
					)}
				</div>
				<DialogFooter>
					<Button type="submit" onClick={handleUpload}>
						<ArrowUpCircleIcon />
						{t("admin.button.upload")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

interface UploadedFileCardProps {
	file: CreateDocument;
	handleFilenameChange: (name: string) => void;
	deleteFile: () => void;
}

const UploadedFileCard = ({
	file,
	handleFilenameChange,
	deleteFile,
}: UploadedFileCardProps) => {
	const [fileName, setFileName] = useState(file.name);
	const [isEditing, setIsEditing] = useState(false);

	const handleBlur = () => {
		setIsEditing(false);
		const newName = capitalizeFirstLetter(removeExtension(fileName));
		setFileName(newName);
		handleFilenameChange(newName);
	};

	return (
		<Card className="p-3 flex items-center justify-between gap-2">
			<div className="flex items-center gap-2 ">
				<FileIcon className="shrink-0" />
				{isEditing ? (
					<form onSubmit={handleBlur} className="flex items-center gap-2">
						<Input
							value={fileName}
							onChange={(e) => setFileName(e.target.value)}
							onBlur={handleBlur}
							autoFocus
						/>
						<Button size="sm" variant="secondary" onClick={() => handleBlur()}>
							<CheckIcon />
						</Button>
					</form>
				) : (
					<Button
						variant="ghost"
						className=" bg-soft-white dark:bg-[#121212] rounded-mb"
						onClick={() => setIsEditing(true)}
					>
						{file.name}
						<PenLineIcon />
					</Button>
				)}
			</div>
			<div className="flex items-center gap-2">
				{!isEditing && (
					<>
						<Button
							size="sm"
							variant="destructive"
							onClick={() => deleteFile()}
						>
							<TrashIcon />
						</Button>
					</>
				)}
			</div>
		</Card>
	);
};
